import * as React from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Footer from '../Materials/Footer';
import AddIcon from '@mui/icons-material/Add';
import Modal from '@mui/material/Modal';
import Pagination from '@mui/material/Pagination';
import BurgerBar from '../Materials/BurgerBar';
import { Link } from 'react-router-dom';

// Componente de Pesquisa e Filtragem para Avisos
function SearchAndFilterAdvices({ onFilterChange, adviceTypes, regions }) {
  const [searchText, setSearchText] = React.useState('');
  const [filterType, setFilterType] = React.useState('');
  const [filterDate, setFilterDate] = React.useState('');
  const [filterRegion, setFilterRegion] = React.useState('');

  const handleFilterChange = () => {
    onFilterChange({
      searchText,
      type: filterType,
      date: filterDate,
      region: filterRegion,
    });
  };

  return (
    <Box sx={{ p: 2, backgroundColor: '#fff', borderBottom: '1px solid #ccc' }}>
      <Container maxWidth="lg">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Search by Title"
              variant="outlined"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel id="filter-type-label">Type of Call</InputLabel>
              <Select
                labelId="filter-type-label"
                label="Type of Advice"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {adviceTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Filtered By Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel id="filter-region-label">Region</InputLabel>
              <Select
                labelId="filter-region-label"
                label="Region"
                value={filterRegion}
                onChange={(e) => setFilterRegion(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {regions.map((region) => (
                  <MenuItem key={region.id} value={region.id}>
                    {region.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Button fullWidth variant="contained" color="primary" onClick={handleFilterChange}>
              Search
            </Button>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

// Componente de Listagem de Avisos
function AdviceList({ filter, adviceTypes, regions }) {
  const [advices, setAdvices] = React.useState([]);
  const [openAdviceModal, setOpenAdviceModal] = React.useState(false);
  const [selectedAdvice, setSelectedAdvice] = React.useState(null);
  const [entityTypes, setEntityTypes] = React.useState([]);

  // Paginação
  const [page, setPage] = React.useState(1);
  const advicesPerPage = 7;

  React.useEffect(() => {
    fetchAdvices();
    fetchEntityTypes();
  }, []);

  // Ao alterar os filtros, volta sempre à primeira página
  React.useEffect(() => {
    setPage(1);
  }, [filter]);

  const compareDates = (date1, date2) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.toDateString() === d2.toDateString();
  };

  // Nova função para buscar tipos de entidade
  const fetchEntityTypes = async () => {
    try {
      const response = await fetch('https://api.hub4food.colab4food.com/api/entity-types');

      if (response.ok) {
        const data = await response.json();
        setEntityTypes(data);
      } else {
        console.error("Failed to fetch entity types:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching entity types:", error);
    }
  };

  const fetchAdvices = async () => {
    try {
      const response = await fetch('https://api.hub4food.colab4food.com/api/advices');

      if (response.ok) {
        const data = await response.json();
        setAdvices(data);
      } else {
        console.error("Failed to fetch advices:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching advices:", error);
    }
  };

  // Filtrar os avisos com base nos filtros
  const filteredAdvices = advices.filter((advice) => {
    return (
      (!filter.searchText || advice.title.toLowerCase().includes(filter.searchText.toLowerCase())) &&
      (!filter.type || advice.advice_type === filter.type) &&
      (!filter.date || compareDates(advice.open_date, filter.date)) &&
      (!filter.region || advice.region_id === filter.region)
    );
  });

  // Paginação dos avisos filtrados
  const totalPages = Math.ceil(filteredAdvices.length / advicesPerPage);
  const startIndex = (page - 1) * advicesPerPage;
  const paginatedAdvices = filteredAdvices.slice(startIndex, startIndex + advicesPerPage);

  // Função para formatar a data (dia e mês abreviado)
  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('-');

    const monthNames = [
      "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
      "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"
    ];

    return {
      day: parseInt(day, 10),
      month: monthNames[parseInt(month, 10) - 1]
    };
  };

  // Função para abrir o modal com as informações do aviso
  const handleOpenAdviceModal = (advice) => {
    setSelectedAdvice(advice);
    setOpenAdviceModal(true);
  };

  // Funções de formatação
  const formatCurrency = (value) => {
    if (!value && value !== 0) return "N.A";

    const num = Number(value);

    if (num >= 1000000) {
      const millions = num / 1000000;

      const formattedMillions = Number.isInteger(millions)
        ? millions.toFixed(0)
        : millions.toFixed(1);

      return `${formattedMillions} M€`;

    } else if (num >= 1000) {
      const thousands = num / 1000;

      const formattedThousands = Number.isInteger(thousands)
        ? thousands.toFixed(0)
        : thousands.toFixed(1);

      return `${formattedThousands} k€`;
    }

    return `${num.toFixed(0)} €`;
  };

  const formatDuration = (hours) => {
    if (!hours && hours !== 0) return "N.A";

    const num = Number(hours);

    if (num < 24) {
      return `${Math.round(num)}h`;

    } else if (num < 730.001) {
      const days = num / 24;
      return Number.isInteger(days) ? `${days}d` : `${days.toFixed(1)}d`;

    } else {
      const months = num / 730.001;
      return Number.isInteger(months) ? `${months}m` : `${months.toFixed(1)}m`;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>

      {paginatedAdvices.map((advice) => {
        const { day, month } = formatDate(advice.submission_period);

        return (
          <Box
            key={advice.id}
            sx={{
              mb: 4,
              p: 2,
              border: '1px solid #ccc',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >

            {/* Quadrado azul com a data */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginRight: '12px',
                alignSelf: 'stretch',
                justifyContent: 'space-between'
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '0.6rem', lineHeight: '1' }}>
                Up to:
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#3FA5D4',
                  color: 'white',
                  borderRadius: '4px',
                  padding: '8px',
                  minWidth: '50px',
                  flex: 1,
                  margin: '4px 0',
                  boxSizing: 'border-box'
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: '1' }}>
                  {day}
                </Typography>

                <Typography variant="caption" sx={{ lineHeight: '1' }}>
                  {month}
                </Typography>
              </Box>
            </Box>

            {/* Detalhes do aviso */}
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'normal' }}>
                {advice.title}
              </Typography>

              <Typography variant="body1" sx={{ color: 'gray' }}>
                {advice.resume}
              </Typography>
            </Box>

            {/* Ícone de "+" para abrir o modal */}
            <IconButton onClick={() => handleOpenAdviceModal(advice)}>
              <AddIcon />
            </IconButton>

          </Box>
        );
      })}

      {/* Paginação */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Modal para exibir informações do aviso */}
      <Modal open={openAdviceModal} onClose={() => setOpenAdviceModal(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            padding: 4,
            boxShadow: 24,
            width: '600px',
            maxHeight: '80vh',
            overflow: 'auto',
          }}
        >
          <Typography variant="h6">Call Details</Typography>

          {selectedAdvice && (
            <>
              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Title:</strong> {selectedAdvice.title}
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Program:</strong> {selectedAdvice.program}
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Open Date:</strong> {new Date(selectedAdvice.open_date).toLocaleDateString()}
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Deadline:</strong> {new Date(selectedAdvice.submission_period).toLocaleDateString()}
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Resume:</strong> {selectedAdvice.resume}
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Maximum Allocation:</strong> {formatCurrency(selectedAdvice.maximum_allocation)}
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Financial Maximum Tax:</strong> {selectedAdvice.financial_maximum_tax || "N/A"}
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Maximum Duration:</strong> {formatDuration(selectedAdvice.maximum_duration)}
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Region:</strong>{' '}
                {regions.find(region => region.id === selectedAdvice.region_id)?.name}
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Call Type:</strong>{' '}
                {adviceTypes.find(type => type.id === selectedAdvice.advice_type)?.type}
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Entity Types:</strong>{' '}
                {selectedAdvice.entity_type_id && selectedAdvice.entity_type_id.length > 0
                  ? selectedAdvice.entity_type_id
                      .map(id => {
                        const entityType = entityTypes.find(type => type.id === id);
                        return entityType ? entityType.name : null;
                      })
                      .filter(Boolean)
                      .join(', ') || "N.A"
                  : "N.A"}
              </Typography>

              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>Link:</strong>{' '}
                <Link
                  to={selectedAdvice.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {selectedAdvice.link}
                </Link>
              </Typography>
            </>
          )}
        </Box>
      </Modal>

    </Container>
  );
}

export default function AdvicesPage() {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [openDrawer, setOpenDrawer] = React.useState(false);
  const [filter, setFilter] = React.useState({});
  const [adviceTypes, setAdviceTypes] = React.useState([]);
  const [regions, setRegions] = React.useState([]);
  const navigate = useNavigate();

  // Buscar tipos de avisos e regiões da API ao carregar o componente
  React.useEffect(() => {
    fetchAdviceTypes();
    fetchRegions();
  }, []);

  const fetchAdviceTypes = async () => {
    try {
      const response = await fetch('https://api.hub4food.colab4food.com/api/advicetypes');

      if (response.ok) {
        const data = await response.json();
        setAdviceTypes(data);
      } else {
        console.error("Failed to fetch advice types:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching advice types:", error);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await fetch('https://api.hub4food.colab4food.com/api/regions');

      if (response.ok) {
        const data = await response.json();
        setRegions(data);
      } else {
        console.error("Failed to fetch regions:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching regions:", error);
    }
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setOpenDrawer(!openDrawer);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setOpenDrawer(false);
  };

  const handleExit = () => {
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      {/* Navbar */}
      <BurgerBar />

      {/* Seção de Pesquisa e Filtragem */}
      <SearchAndFilterAdvices
        onFilterChange={handleFilterChange}
        adviceTypes={adviceTypes}
        regions={regions}
      />

      {/* Listagem de Avisos */}
      <AdviceList
        filter={filter}
        adviceTypes={adviceTypes}
        regions={regions}
      />

      <Box>
        <Footer />
      </Box>
    </Box>
  );
}